/**
 * Getting two browsers through to each other.
 *
 * Our server holds the introduction and nothing else - see
 * `src/core/net/signal.ts` for what it will and will not pass on. This is
 * the part that happens in the page: make an offer, send it up, get an
 * answer back, and end up holding a `Wire` that goes straight to the other
 * machine.
 *
 * **Both sides talk to our server over the ordinary game socket.** No second
 * address, no second port, and the same door limits: the signalling
 * messages are `host`, `offer`, `answer`, `ice` and `through`, and they ride
 * the connection the home screen already opens to read the world list.
 *
 * **Without a STUN server, this works on one network and not between two.**
 * Two machines behind two routers each believe their address is `192.168.x`,
 * which is no use to the other one; a STUN server is the thing that tells
 * each of them what the world sees. `/ice` is where the page asks ours what
 * to use, and an empty answer means a game on the kitchen network.
 */
import type { Wire } from "../core/net/link.js";
import { channelWire } from "./channel.js";
import { encode, decode, PROTOCOL_VERSION } from "../core/net/protocol.js";

/** How long to wait for the far end before giving up on them. */
const PATIENCE_MS = 20_000;

/**
 * What to tell RTCPeerConnection about getting out of here.
 *
 * Asked of our own server rather than written in, so that adding a STUN box
 * is a line in the unit file and not a release. A failed ask is an empty
 * list, which is a game on one network: the worst it can do is fail to
 * connect, and a page that would not load because of it would be worse.
 */
export async function iceServers(from: string): Promise<RTCIceServer[]> {
  try {
    /*
     * The address we have is the world's, which is `ws://` or `wss://`, and
     * fetch will not touch either. The page and the world are one machine
     * and one port - see the note over the http server - so the http form
     * of the same address is where to ask.
     */
    const over = from.replace(/^ws/, "http");
    const answer = await fetch(new URL("/ice", over).href);
    if (!answer.ok) return [];
    const said = await answer.json() as { iceServers?: RTCIceServer[] };
    return Array.isArray(said.iceServers) ? said.iceServers : [];
  } catch {
    return [];
  }
}

/**
 * Our end of the signalling socket.
 *
 * One socket, several conversations: the host has one of these and every
 * person calling them arrives down it, told apart by `from`.
 */
export class Signal {
  private socket: WebSocket | null = null;
  /** What to do with each kind of message, by `t`. */
  private readonly ears = new Map<string, (m: Record<string, unknown>) => void>();

  constructor(private readonly url: string) {}

  open(): Promise<boolean> {
    return new Promise((resolve) => {
      let socket: WebSocket;
      try {
        socket = new WebSocket(this.url);
      } catch {
        resolve(false);
        return;
      }
      this.socket = socket;
      const timer = setTimeout(() => resolve(false), 6000);
      socket.addEventListener("open", () => {
        clearTimeout(timer);
        resolve(true);
      });
      socket.addEventListener("message", (event) => {
        if (typeof event.data !== "string") return;
        const message = decode(event.data) as Record<string, unknown> | null;
        if (message === null || typeof message.t !== "string") return;
        this.ears.get(message.t)?.(message);
      });
      socket.addEventListener("error", () => { clearTimeout(timer); resolve(false); });
      socket.addEventListener("close", () => { this.socket = null; });
    });
  }

  hear(t: string, fn: (m: Record<string, unknown>) => void): void {
    this.ears.set(t, fn);
  }

  say(message: object): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode(message as never));
  }

  /** Say one thing and wait for one kind of answer. */
  ask(
    message: object, t: string, within = PATIENCE_MS,
  ): Promise<Record<string, unknown> | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), within);
      this.hear(t, (m) => { clearTimeout(timer); resolve(m); });
      this.say(message);
    });
  }

  close(): void {
    this.socket?.close();
    this.socket = null;
  }
}

/**
 * Holding a world open for other people.
 *
 * Puts a row in our server's list, then waits. Every `call` that comes down
 * is somebody who picked that row: a peer connection is made for them, the
 * answer goes back the same way, and when the channel opens `onWire` is
 * handed a wire that has nothing of ours in it.
 */
export class Doorbell {
  onWire: ((wire: Wire, from: number) => void) | null = null;
  /** The name our server gave this world in its list. */
  world = "";
  private readonly calls = new Map<number, RTCPeerConnection>();
  private readonly queues = new Map<number, IceQueue>();
  private readonly early = new Map<number, string[]>();
  private ice: RTCIceServer[] = [];

  constructor(private readonly signal: Signal, private readonly from: string) {}

  /**
   * Put it on the list, and start listening.
   *
   * Answers with the world's name in the list, or null with the reason -
   * "sign-in" where the account did not check out, "not-here" where the
   * server is not introducing anybody at all.
   */
  async hold(
    map: string, name: string, token: string, locked: boolean,
    /*
     * A token if the page has one, a password if it has only that - the two
     * ways our door already takes, and for the same reason: asking for a
     * token alone would mean logging in somewhere else and coming back.
     */
    password = "",
  ): Promise<{ world: string } | { why: string }> {
    this.ice = await iceServers(this.from);
    const said = await this.signal.ask(
      { t: "host", map, name, token, locked, password }, "hosting");
    if (said === null) return { why: "unreachable" };
    if (said.ok !== true || typeof said.world !== "string") {
      return { why: String(said.why ?? "no") };
    }
    this.world = said.world;
    this.signal.hear("call", (m) => { void this.answer(m); });
    this.signal.hear("ice", (m) => {
      const from = Number(m.from);
      // A candidate can even beat the call it belongs to; held for it.
      let queue = this.queues.get(from);
      if (queue === undefined) {
        const early = this.early.get(from) ?? [];
        early.push(String(m.line));
        this.early.set(from, early);
        return;
      }
      queue.add(String(m.line));
    });
    return { world: said.world };
  }

  /** How many are in there, so the row in the list says something true. */
  say(players: number): void {
    if (this.world.length === 0) return;
    this.signal.say({ t: "holding", world: this.world, players });
  }

  private async answer(message: Record<string, unknown>): Promise<void> {
    const from = Number(message.from);
    if (!Number.isFinite(from)) return;
    const peer = new RTCPeerConnection({ iceServers: this.ice });
    this.calls.set(from, peer);
    const queue = new IceQueue(peer);
    this.queues.set(from, queue);
    for (const line of this.early.get(from) ?? []) queue.add(line);
    this.early.delete(from);
    /*
     * The guest opens the channel, not us.
     *
     * One side has to, and it has to be the same side every time or both
     * ends make one and the world is held down two wires at once. The
     * caller opens; the callee waits for `datachannel`.
     */
    peer.addEventListener("datachannel", (event) => {
      this.onWire?.(channelWire(event.channel), from);
    });
    peer.addEventListener("icecandidate", (event) => {
      if (event.candidate === null) return;
      this.signal.say({ t: "ice", to: from, line: event.candidate.candidate });
    });
    try {
      await peer.setRemoteDescription({
        type: "offer", sdp: String(message.sdp),
      });
      queue.open();
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      this.signal.say({ t: "answer", to: from, sdp: answer.sdp ?? "" });
    } catch {
      this.calls.delete(from);
      this.queues.delete(from);
      peer.close();
    }
  }

  /** Take the row off the list and let everybody go. */
  stop(): void {
    this.signal.say({ t: "unhost" });
    for (const peer of this.calls.values()) peer.close();
    this.calls.clear();
    this.queues.clear();
    this.early.clear();
    this.world = "";
  }
}

/**
 * Knocking on a world that is on somebody else's machine.
 *
 * Makes the offer, sends it up, waits for the answer, and gives back a wire
 * to that machine - or null, because a world on a laptop may have gone
 * between the list being drawn and the row being chosen, and failing to get
 * through to somebody must never be worse than a sentence on the screen.
 */
/**
 * Candidates that arrive before the other side's description does.
 *
 * `addIceCandidate` refuses a candidate until the remote description is
 * set, and both ends were adding them the moment they arrived - which is
 * before, because the first candidates are sent the instant a description
 * is made. The refusal was swallowed, and the ones lost were the local
 * addresses, gathered first: two machines on one Wi-Fi were left with only
 * their shared public address, which a home router does not loop back.
 * "З іншого комп'ютера та сама помилка. Один wifi на двох."
 */
class IceQueue {
  private waiting: string[] = [];
  private ready = false;
  constructor(private readonly peer: RTCPeerConnection) {}
  add(line: string): void {
    if (!this.ready) { this.waiting.push(line); return; }
    void this.peer.addIceCandidate({ candidate: line, sdpMid: "0" })
      .catch(() => { /* a candidate that does not fit is not fatal */ });
  }
  /** The remote description is set: everything held back goes in. */
  open(): void {
    this.ready = true;
    for (const line of this.waiting.splice(0)) this.add(line);
  }
}

/**
 * How the last knock ended, when it did not get through: the host never
 * answered the call, or answered and no path between the two machines
 * opened. Two different faults - a world that has gone quiet, and two
 * networks that cannot reach each other without a relay - and "не
 * відповідає" for both sent the looking in the wrong place.
 */
export let lastKnock: "silent" | "no-path" | null = null;

export async function knock(
  signal: Signal, from: string, world: string,
): Promise<Wire | null> {
  lastKnock = null;
  const peer = new RTCPeerConnection({ iceServers: await iceServers(from) });
  // What was tried, for the console: which kinds of address each side
  // offered, and where the connection got to.
  const kinds = { mine: new Set<string>(), theirs: new Set<string>() };
  const typeOf = (line: string): string => / typ (\w+)/.exec(line)?.[1] ?? "?";
  peer.addEventListener("iceconnectionstatechange", () => {
    console.log(`knock ice: ${peer.iceConnectionState}`);
  });
  /*
   * Ordered and reliable, which is the default, said out loud because it is
   * the whole reason this can carry the same messages as a WebSocket.
   */
  const channel = peer.createDataChannel("гра", { ordered: true });
  const wire = channelWire(channel);

  peer.addEventListener("icecandidate", (event) => {
    if (event.candidate === null) return;
    kinds.mine.add(typeOf(event.candidate.candidate));
    signal.say({ t: "ice", line: event.candidate.candidate });
  });
  const queue = new IceQueue(peer);
  signal.hear("ice", (m) => {
    kinds.theirs.add(typeOf(String(m.line)));
    queue.add(String(m.line));
  });

  const opened = new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), PATIENCE_MS);
    channel.addEventListener("open", () => { clearTimeout(timer); resolve(true); });
    channel.addEventListener("error", () => { clearTimeout(timer); resolve(false); });
  });

  try {
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    const said = await signal.ask(
      { t: "offer", world, sdp: offer.sdp ?? "" }, "answer");
    if (said === null || typeof said.sdp !== "string") {
      lastKnock = "silent";
      console.log("knock: the host did not answer the call");
      peer.close();
      return null;
    }
    await peer.setRemoteDescription({ type: "answer", sdp: said.sdp });
    queue.open();
  } catch {
    peer.close();
    return null;
  }

  if (!await opened) {
    lastKnock = "no-path";
    console.log(`knock: answered, but no path opened - ice ${peer.iceConnectionState}, ` +
      `mine [${[...kinds.mine].join(" ")}], theirs [${[...kinds.theirs].join(" ")}]`);
    peer.close();
    return null;
  }
  // We are through; our server may forget about us.
  signal.say({ t: "through" });
  return wire;
}

/** The version the page speaks, for a hello down a wire. */
export { PROTOCOL_VERSION };
